import {
  WORK_ORDER_SEVERITIES,
  WORK_ORDER_SEVERITY_LABELS,
  type WorkOrderSeverity,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { fetchOrgSettings, patchOrgSettings } from '../../../api/org-settings.js';
import { ApiError } from '../../../lib/api-client.js';

export function WorkOrdersSettingsPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['org-settings'], queryFn: fetchOrgSettings });
  const [prefix, setPrefix] = useState('WO');
  const [useBuildingPrefix, setUseBuildingPrefix] = useState(false);
  const [defaultSeverity, setDefaultSeverity] = useState<WorkOrderSeverity>('medium');
  const [requirePhotoOnCompletion, setRequirePhotoOnCompletion] = useState(true);
  const [allowSelfAssignment, setAllowSelfAssignment] = useState(true);

  useEffect(() => {
    const wo = settingsQuery.data?.settings.workOrders;
    if (wo) {
      setPrefix(wo.workOrderPrefix);
      setUseBuildingPrefix(wo.useBuildingPrefix);
      setDefaultSeverity(wo.defaultSeverity);
      setRequirePhotoOnCompletion(wo.requirePhotoOnCompletion);
      setAllowSelfAssignment(wo.allowSelfAssignment);
    }
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      patchOrgSettings({
        workOrders: {
          workOrderPrefix: prefix,
          useBuildingPrefix,
          defaultSeverity,
          requirePhotoOnCompletion,
          allowSelfAssignment,
        },
      }),
    onSuccess: () => {
      toast.success('Work-order settings saved');
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not save settings'),
  });

  if (settingsQuery.isLoading) return <LoadingState rows={5} />;
  if (settingsQuery.isError) return <ErrorState description="Could not load settings." />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Work-order settings</h2>
        <p className="text-sm text-fg-secondary">
          Tune numbering, defaults, and completion requirements.
        </p>
      </div>

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="space-y-4"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Numbering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="prefix">Prefix</Label>
              <Input
                id="prefix"
                maxLength={8}
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
              />
              <p className="text-xs text-fg-muted">
                Becomes part of every WO display number, e.g. <code>{prefix || 'WO'}-00042</code>.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={useBuildingPrefix}
                onChange={(e) => setUseBuildingPrefix(e.target.checked)}
              />
              Include a building short-code in the display number
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Defaults</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="def-sev">Default severity</Label>
              <select
                id="def-sev"
                value={defaultSeverity}
                onChange={(e) => setDefaultSeverity(e.target.value as WorkOrderSeverity)}
                className="h-10 rounded border border-border bg-surface px-3 text-sm"
              >
                {WORK_ORDER_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {WORK_ORDER_SEVERITY_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={allowSelfAssignment}
                onChange={(e) => setAllowSelfAssignment(e.target.checked)}
              />
              Allow team members to claim unassigned work orders routed to their team
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={requirePhotoOnCompletion}
                onChange={(e) => setRequirePhotoOnCompletion(e.target.checked)}
              />
              Require a completion photo before a work order can be marked complete
            </label>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </form>
    </div>
  );
}
